import React, { useState, useEffect, useRef } from 'react'
import ReactDOM from 'react-dom'
import { calcStars, STAR_COLORS } from '@/Components/Stars'
import { useIsMobile } from '@/hooks/useIsMobile'
import { POSITION_COLORS } from '@/Components/Cards/positionColors'
import GlitchMark from './GlitchMark'
import HoverTooltip from '@/Components/HoverTooltip'

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api'

// Inject card animation keyframes once
if (typeof document !== 'undefined' && !document.getElementById('card-fx-keyframes')) {
  const style = document.createElement('style')
  style.id = 'card-fx-keyframes'
  style.textContent = `
    @keyframes matchGlowPulse {
      0%, 100% { box-shadow: 0 0 8px var(--glow-color), 0 0 18px var(--glow-color-soft); }
      50% { box-shadow: 0 0 14px var(--glow-color-bright), 0 0 28px var(--glow-color); }
    }
    @keyframes cardShimmer {
      0% { transform: translateX(-100%) rotate(25deg); }
      100% { transform: translateX(200%) rotate(25deg); }
    }
    @keyframes holoSpin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes editionGlow {
      0%, 100% { opacity: 0.4; }
      50% { opacity: 0.8; }
    }
    @keyframes holoEdge {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
    @keyframes sparkle {
      0%, 100% { opacity: 0; transform: scale(0); }
      50% { opacity: 1; transform: scale(1); }
    }
    @keyframes diamondBorder {
      0% { background-position: 0% 50%; }
      100% { background-position: 200% 50%; }
    }
  `
  document.head.appendChild(style)
}

const hexToRgba = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

// ─── Edition visual config ─────────────────────────────────────────────────
export const EDITION_STYLES: Record<string, {
  borderColor: string
  bgGradient: string
  labelColor: string
  label: string
  rarity: string
  glowColor?: string
}> = {
  // 'base' — the "no-effect" floor print that just fields the player for their FP.
  // Flattest/mattest treatment, no foil (see edition FX gating). Shown as "Base".
  base: {
    borderColor: '#414a5c',
    bgGradient: 'linear-gradient(140deg, #2b3242 0%, #232a36 60%, #2a313f 100%)',
    labelColor: '#8895a9',
    label: 'Base',
    rarity: 'Basic',
  },
  // Metallic (slug 'metallic') — the first effect tier. Brushed-steel treatment with a
  // cool silver sheen band + a slow light sweep (see edition FX gating), a clear rung
  // above the flat no-effect floor print ('base', shown as "Base") but below the
  // rainbow Holographic.
  metallic: {
    borderColor: '#8a9bb4',
    bgGradient: 'linear-gradient(140deg, #2c3441 0%, #38414f 30%, #434c5b 45%, #313945 60%, #3a4350 82%, #272d38 100%)',
    labelColor: '#c3cedd',
    label: 'Metallic',
    rarity: 'Common',
    glowColor: 'rgba(154,171,196,0.24)',
  },
  holographic: {
    borderColor: '#a78bfa',
    bgGradient: 'linear-gradient(140deg, #241a5e 0%, #3a1673 48%, #221a52 100%)',
    labelColor: '#c4b5fd',
    label: 'Holographic',
    rarity: 'Uncommon',
    glowColor: 'rgba(167,139,250,0.35)',
  },
  prismatic: {
    borderColor: '#ec4899',
    bgGradient: 'linear-gradient(140deg, #3a1173 0%, #8a1f52 38%, #1e3a5f 70%, #075e4b 100%)',
    labelColor: '#f9a8d4',
    label: 'Prismatic',
    rarity: 'Rare',
    glowColor: 'rgba(236,72,153,0.4)',
  },
  diamond: {
    borderColor: '#67e8f9',
    bgGradient: 'linear-gradient(140deg, #0c4a6e 0%, #136a86 38%, #1e3a5f 68%, #0e7490 100%)',
    labelColor: '#a5f3fc',
    label: 'Diamond',
    rarity: 'Ultra-Rare',
    glowColor: 'rgba(103,232,249,0.45)',
  },
}

/** A synthetic card's treatment: the EDITION'S OWN COLORS, muted.
 *
 * ⚠️ A TREATMENT, NEVER A SEPARATE PALETTE. A synthetic diamond has to read as
 * diamond-and-manufactured; give it colors of its own and it becomes a sixth edition,
 * which is exactly the wrong story — the effect really IS diamond-strength, on a
 * diamond bar. Only the provenance differs, so only the saturation does.
 *
 * ⚠️ The card is minted at the effect's edition, so `card.edition` alone cannot tell a
 * built card from a pulled one. The `synthetic` flag is the only thing that can, which is
 * why the backend serializes it beside the edition rather than expecting a derivation.
 */
export const syntheticStyle = (base: typeof EDITION_STYLES[string]) =>
  syntheticStyleFor(base) as typeof EDITION_STYLES[string]

/** ⚠️ A SYNTHETIC IS NOT AN EDITION, AND THAT REVERSES WHAT THIS FILE USED TO SAY. The
 *  original rule was "a treatment, never a separate palette — a synthetic diamond has to
 *  read as diamond-and-manufactured". Owner ruling 2026-08-30 overturns it: all that
 *  happened was an effect being taken, so wearing the donor edition's identity overstates
 *  it. A synthetic is its own thing and looks like its own thing.
 *
 *  ⚠️ TWO SUBTRACTIVE ATTEMPTS FAILED FIRST, and the reason is worth keeping. It was
 *  `saturate(0.55) brightness(0.9)` over the whole card face, text included — and the
 *  brightness term does the same visual job as the INACTIVE treatment (`opacity: 0.7`), so
 *  a good synthetic read as an expired card. Dropping the brightness and easing saturation
 *  to 0.6 did not fix it either: DESATURATION IS ITSELF SUBTRACTIVE. Draining colour and
 *  fading out are the same gesture to the eye, and no amount of "less" reads as
 *  "manufactured" rather than "expired". The blueprint is ADDITIVE — it is a look, not a
 *  reduction of one.
 *
 *  ⚠️ ONE PALETTE FOR EVERY SYNTHETIC, whatever edition the effect came from. That is the
 *  whole point of the ruling: the tier lives in the effect's power, its gate and its
 *  tooltip, not in the card's colour. */
/** ⚠️ THE GROUND IS CONSTANT, THE LINE WORK IS THE EDITION'S (owner). The blueprint says
 *  "this was built"; the colour of the lines says which tier the effect it took came from.
 *  So a synthetic diamond is a diamond-blue schematic and a synthetic metallic a steel one
 *  — same form, different ink — and neither can be mistaken for a real pull, because a real
 *  pull has the edition's GROUND and its foil.
 *
 *  ⚠️ The dark ground is what must not vary. It is the only part carrying "synthetic", and
 *  letting the edition tint it slides straight back to the treatment this replaced. */
export const SYNTHETIC_GROUND =
  'linear-gradient(140deg, #0b1620 0%, #0e1d29 55%, #0a141d 100%)'

export const syntheticStyleFor = (base: typeof EDITION_STYLES[string]) => ({
  borderColor: base.borderColor,
  bgGradient: SYNTHETIC_GROUND,
  labelColor: base.labelColor,
  label: 'SYNTHETIC',
  rarity: 'Synthetic',
  glowColor: undefined,
})

/** The schematic grid, in the edition's own colour. Generated rather than an asset, so it
 *  survives the small card sizes. ⚠️ The alpha is a hex suffix on the edition colour, which
 *  requires every `borderColor` to be a 6-digit hex — they all are. */
export const syntheticGrid = (color: string) =>
  `repeating-linear-gradient(0deg, ${color}1f 0 1px, transparent 1px 12px), `
  + `repeating-linear-gradient(90deg, ${color}1f 0 1px, transparent 1px 12px)`

const POSITION_LABELS: Record<number, string> = {
  1: 'QB', 2: 'RB', 3: 'WR', 4: 'TE', 5: 'K',
}


// Category colors for the effect badge
const CATEGORY_COLORS: Record<string, string> = {
  flat_fp: '#4ade80',      // green
  multiplier: '#a78bfa',   // purple
  floobits: '#eab308',     // gold
  conditional: '#60a5fa',  // blue
  streak: '#fb923c',       // orange
}

// Behavior tags — shown on cards so users know which modifiers affect them
const BEHAVIOR_TAGS: Record<string, {
  label: string
  color: string
  tooltip: string
}> = {
  chance: {
    label: 'Chance',
    color: '#c084fc',      // light purple
    tooltip: 'Chance — Has a random trigger roll. Boosted by Fortunate modifier.',
  },
  conditional: {
    label: 'Conditional',
    color: '#60a5fa',      // blue
    tooltip: 'Conditional — Triggers when a game condition is met. Doubled by Longshot modifier.',
  },
  streak: {
    label: 'Streak',
    color: '#fb923c',      // orange
    tooltip: 'Streak — Grows each week a condition holds, resets when broken. Protected by Ironclad modifier.',
  },
}

// Classification badge config (perks from card classifications)
const CLASSIFICATION_CONFIG: Record<string, {
  label: string
  abbr: string
  color: string
  bgColor: string
  borderColor: string
  tooltip: string
}> = {
  // Solid deep-jewel fills with near-white ink — legible on any edition (incl.
  // MVP on the diamond). `bgColor` is the fill, `color` the ink, `borderColor` a
  // subtle light rim.
  rookie: {
    label: 'Rookie', abbr: 'R', color: '#f8fafc',
    bgColor: '#a16207', borderColor: 'rgba(255,255,255,0.2)',
    tooltip: 'Rookie: sells for 2x Floobits',
  },
  mvp: {
    label: 'MVP', abbr: 'MVP', color: '#f8fafc',
    bgColor: '#2563eb', borderColor: 'rgba(255,255,255,0.2)',
    tooltip: 'MVP: unlocks the FLEX lineup slot',
  },
  champion: {
    label: 'Champion', abbr: 'CH', color: '#f8fafc',
    bgColor: '#c2410c', borderColor: 'rgba(255,255,255,0.2)',
    tooltip: 'Champion: boosts your FPx when stacked with same-team cards',
  },
  all_pro: {
    label: 'All-Pro', abbr: 'AP', color: '#f8fafc',
    bgColor: '#7c3aed', borderColor: 'rgba(255,255,255,0.2)',
    tooltip: 'All-Pro: lowers its FP power-bar threshold',
  },
}

function parseClassifications(classification?: string | null, isRookie?: boolean,
                              synthetic?: boolean): string[] {
  // ⚠️ A SYNTHETIC NEVER WEARS AN ACCOLADE IT DID NOT EARN (owner). The mint clears
  // `classification`, but ROOKIE lives in its own column and was copied from the source
  // template — so a transplant onto a base card came back wearing the R tag. Fixed at the
  // mint too; this guard is what clears the synthetics ALREADY MINTED under the old rule,
  // with no migration.
  if (synthetic) return []
  if (classification) {
    return ['rookie', 'mvp', 'champion', 'all_pro'].filter(
      key => classification.includes(key)
    )
  }
  // Backward compat: fall back to isRookie flag
  if (isRookie) return ['rookie']
  return []
}

// FP-type colors for colorizing effect text
const TYPE_COLORS: Record<string, string> = {
  fp: '#4ade80',       // green
  mult: '#f472b6',     // pink — FPx
  floobits: '#eab308', // gold
}

/**
 * Parse text for FP-type keywords and wrap them + their numeric prefix in colored spans.
 */
function colorizeEffectText(text: string, baseColor: string): React.ReactNode {
  // Determine dominant color from keywords in the text
  const dominantColor = /FPx|\+FPx/.test(text) ? TYPE_COLORS.mult
    : /Floobits/.test(text) ? TYPE_COLORS.floobits
    : /FP\b/.test(text) ? TYPE_COLORS.fp
    : null
  // Match: keyword with optional leading number/range, OR standalone signed numbers
  const pattern = /([+-]?\d+\.?\d*x?[–\-]\d+\.?\d*x?\s*|[+-]?\d+\.?\d*x?\s*)?(\+?FPx|FP\b|Floobits\b|F\b)|(?<=[,\s]|^)([+-]\d+\.?\d*x?)(?=\s|$|,)/g
  const parts: React.ReactNode[] = []
  let lastIdx = 0
  let m: RegExpExecArray | null
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index))
    if (m[2]) {
      // Keyword match — color by keyword type
      const kw = m[2]
      const color = (kw === 'FPx' || kw === '+FPx') ? TYPE_COLORS.mult
        : (kw === 'Floobits' || kw === 'F') ? TYPE_COLORS.floobits
        : TYPE_COLORS.fp
      parts.push(<span key={m.index} style={{ color }}>{m[0]}</span>)
    } else if (m[3]) {
      // Standalone signed number — use dominant color
      const color = dominantColor || TYPE_COLORS.fp
      parts.push(<span key={m.index} style={{ color }}>{m[3]}</span>)
    }
    lastIdx = m.index + m[0].length
  }
  if (parts.length === 0) return text
  if (lastIdx < text.length) parts.push(text.slice(lastIdx))
  return <>{parts}</>
}

export interface CardData {
  id: number
  templateId: number
  playerId: number
  playerName: string
  teamId: number | null
  teamColor: string | null
  playerRating: number
  ratingStars?: number  // precomputed star count (falls back to calcStars)
  position: number
  edition: string
  tier?: number  // Upgrade tier 1-4 (I-IV); ribbon shown for 2+, gold ring at 4
  tierNote?: string | null  // distinct line, e.g. "Tier 4: +90 FP" or "Tier 4: ×1.5 output"
  seasonCreated: number
  isRookie: boolean
  classification?: string | null
  effectConfig: any
  effectName?: string
  displayName?: string
  category?: string
  outputType?: string
  tagline?: string
  tooltip?: string
  detail?: string
  gateText?: string          // the FP power-bar requirement, shown on its own line
  validPositions?: number[]  // positions this effect can transplant onto (1-5)
  sellValue: number
  combineValue?: number      // classification-aware value used by The Combine / picker sort
  isActive: boolean
  isEquipped?: boolean
  vaulted?: boolean  // permanently in the Vault — can't equip/sell/combine
  // ⚠️ Synthetic: a manufactured pairing — a real player, a real effect, built rather
  // than pulled. It is minted at the EFFECT's edition, so `edition` alone cannot tell it
  // from a genuine pull; this flag is the only thing that can. Renders in the edition's
  // own color, MUTED, with a SNTH tag.
  synthetic?: boolean
  // A floor print taken from the universal base pool, not owned. Carries `templateId`
  // and `id: 0` — the equip path sends the template and the server materializes the row.
  fromPool?: boolean
  // Glitch (docs/GLITCH_CARDS.md): marked during a Criticality. Purely a visual flag
  // here — the extra payout is resolved server-side at week end.
  glitched?: boolean
  glitchedSeason?: number | null
  glitchedWeek?: number | null
  // True when the on-card player is AWAKENED, which converges the treatment on the
  // existing gold rather than the unstable violet.
  glitchAwakened?: boolean
  // The week a surge actually fired — the one moment the card is allowed to move.
  glitchSurged?: boolean
  // Player's stat line for the card's season — shown on the back of a vaulted
  // card (which drops its effect and becomes a keepsake player card).
  playerStats?: {
    season: number
    teamName?: string | null
    teamColor?: string | null
    fantasyPoints: number
    // Per game, alongside the total. A season total says how MUCH a player produced;
    // the average says how good they were, which is the comparison a card back invites.
    // Null when the season recorded no games — an average there is a divide by zero
    // dressed up as 0.0.
    gamesPlayed?: number
    fantasyPointsPerGame?: number | null
    lines: { label: string; value: number | string }[]
  } | null
  acquiredAt: string | null
  acquiredVia: string
  // Pack-reveal only: how many of this effect the user already owns (non-vaulted).
  // >0 means keeping this card yields a same-effect duplicate (Level Up fuel).
  ownedEffectCount?: number
  // Showcase-context only: this card's scoring breakdown + its Floobit share of
  // the weekly dividend (attached by the showcase endpoints).
  showcase?: {
    editionPoints: number
    classificationPoints: number
    recency: number
    tierMult: number
    points: number
    dividend: number
  }
}

interface TradingCardProps {
  card: CardData
  size?: 'xs' | 'sm' | 'md' | 'lg'
  selected?: boolean
  /** Toggles selection. Gets the click so a caller can read shift for range select. */
  onSelect?: (e: React.MouseEvent) => void
  onClick?: () => void
  onLevelUp?: () => void  // shows a "Level Up" affordance (collection view)
  onTrash?: () => void    // shows a "Trash" affordance (vault view — permanent delete)
  onFindDupes?: () => void  // shows a "duplicates" affordance (collection view)
  dupeCount?: number      // OTHER owned cards with this effect; the affordance hides at 0
  showSellValue?: boolean
  glowColor?: string  // persistent outline/glow (e.g. team color for roster match)
  staticGlow?: boolean  // if true, glow without pulse animation (for deck cards)
  noHoverLift?: boolean  // disable translateY on hover (parent handles it)
  onHoverChange?: (hovered: boolean) => void
  forceFlipped?: boolean  // externally control flip state (e.g. tutorial)
  // For All-Pro cards in equipped context: 'active' = swap grant available,
  // 'used' = grant already consumed. Undefined for non-equipped cards or
  // non-All-Pro cards — badge renders normally.
  apSwapState?: 'active' | 'used'
  // The depicted player's live week FP, for the FP power-bar gate. When provided
  // (scoring/lineup context) the card shows a live fill + active/OFF state; when
  // omitted (collection/shop) it shows the static requirement only.
  gateFP?: number
}

const SIZES = {
  xs: { width: 105, height: 178, font: 9, nameFont: 11, avatar: 46, pad: 6, starSize: 17 },
  sm: { width: 160, height: 270, font: 12, nameFont: 14, avatar: 66, pad: 8, starSize: 24 },
  md: { width: 200, height: 340, font: 14, nameFont: 17, avatar: 84, pad: 12, starSize: 31 },
  lg: { width: 260, height: 430, font: 16, nameFont: 20, avatar: 110, pad: 16, starSize: 38 },
}

// Tier badge (hexagon) dimensions per card size. Pinned just under the header
// divider, top-left of the body. `top` clears the header (≈ 2×(pad-2) + label).
const TIER_BADGE_DIMS = {
  xs: { top: 25, left: 6, w: 20, h: 17, font: 7 },
  sm: { top: 32, left: 8, w: 23, h: 20, font: 8 },
  md: { top: 42, left: 10, w: 27, h: 23, font: 10 },
  lg: { top: 53, left: 13, w: 32, h: 27, font: 12 },
}

const TIER_ROMAN: Record<number, string> = { 1: 'I', 2: 'II', 3: 'III', 4: 'IV' }

// Glitch marker (docs/GLITCH_CARDS.md). A breathing rim alone cannot carry "this card is
// different" at a glance in a lineup of six — it reads as ambient card art. This is the
// unmistakable part; the rim is the atmosphere. Mirrors the tier badge's corner placement
// on the opposite side so the two never collide.
// Vertical offsets mirror TIER_BADGE_DIMS so the mark clears the top badge row (the
// season chip lives there) and sits opposite the tier hexagon rather than on top of it.
// `font` is gone: the mark is an SVG now and sizes itself from the box (GLYPH_FILL in
// GlitchMark), rather than from a font-size tuned for block characters.
const GLITCH_MARK_DIMS = {
  xs: { top: 25, right: 6, size: 12 },
  sm: { top: 32, right: 8, size: 15 },
  md: { top: 42, right: 10, size: 18 },
  lg: { top: 53, right: 13, size: 22 },
}
const HEX_CLIP = 'polygon(25% 0, 75% 0, 100% 50%, 75% 100%, 25% 100%, 0 50%)'

// Delegate to calcStars + STAR_COLORS so the card's tier color can't drift out
// of sync with the star count it displays. Previously hardcoded thresholds here
// used different cutoffs (95/85/75/65) than the backend star bands (92/84/76/68),
// which caused e.g. an 84-rated player to render 4 stars but in 3-star blue.
const getTierColor = (rating: number): string => STAR_COLORS[calcStars(rating)]

const ClassificationBadge: React.FC<{
  abbr: string
  color: string
  bgColor: string
  borderColor: string
  tooltip: string
  fontSize: number
  // For All-Pro badges in equipped context: 'active' = unused swap available,
  // 'used' = swap consumed this game day. Undefined = no swap state to show.
  swapState?: 'active' | 'used'
}> = ({ abbr, color, bgColor, borderColor, tooltip, fontSize, swapState }) => {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const ref = useRef<HTMLSpanElement>(null)

  const handleEnter = () => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setPos({ x: rect.left + rect.width / 2, y: rect.top })
    setShow(true)
  }

  // Dim the badge when the All-Pro swap grant has already been used this
  // game day so users can see at a glance which AP cards still carry an
  // unused swap.
  const isUsed = swapState === 'used'
  const badgeOpacity = isUsed ? 0.45 : 1
  // Tooltip override for AP swap state
  const effectiveTooltip = swapState === 'active' ? 'All-Pro — Swap available'
    : swapState === 'used' ? 'All-Pro — Swap used this game day'
    : tooltip
  // Dot color: green for active, gray for used
  const dotColor = swapState === 'active' ? '#22c55e'
    : swapState === 'used' ? '#64748b'
    : null

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{
          fontSize, fontWeight: '800', color,
          backgroundColor: bgColor, padding: '2px 5px',
          borderRadius: '4px', border: `1px solid ${borderColor}`,
          boxShadow: '0 1px 2px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.22)',
          cursor: 'default',
          opacity: badgeOpacity,
          display: 'inline-flex', alignItems: 'center', gap: '3px',
          transition: 'opacity 0.2s',
        }}
      >
        {abbr}
        {dotColor && (
          <span
            style={{
              width: '5px', height: '5px',
              borderRadius: '50%',
              backgroundColor: dotColor,
              display: 'inline-block',
              boxShadow: swapState === 'active' ? `0 0 4px ${dotColor}` : 'none',
              flexShrink: 0,
            }}
          />
        )}
      </span>
      {show && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          left: pos.x,
          top: pos.y - 8,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#0f172a',
          border: `1px solid ${borderColor}`,
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '11px',
          color,
          lineHeight: '1',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          zIndex: 10010,
          pointerEvents: 'none',
          fontFamily: 'pressStart',
          whiteSpace: 'nowrap',
        }}>
          {effectiveTooltip}
        </div>,
        document.body
      )}
    </>
  )
}

const EditionBadge: React.FC<{
  label: string
  rarity: string
  color: string
  fontSize: number
  tooltipOverride?: string
}> = ({ label, rarity, color, fontSize, tooltipOverride }) => {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const ref = useRef<HTMLSpanElement>(null)

  const handleEnter = () => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setPos({ x: rect.left + rect.width / 2, y: rect.top })
    setShow(true)
  }

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{
          fontSize, fontWeight: '700', color,
          textTransform: 'uppercase', letterSpacing: '0.02em',
          cursor: 'default',
        }}
      >
        {label}
      </span>
      {show && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          left: pos.x,
          top: pos.y - 8,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#0f172a',
          border: '1px solid #334155',
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '11px',
          color,
          lineHeight: '1',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          zIndex: 10010,
          pointerEvents: 'none',
          fontFamily: 'pressStart',
          whiteSpace: 'nowrap',
        }}>
          {tooltipOverride ?? rarity}
        </div>,
        document.body
      )}
    </>
  )
}

export function getBehaviorTag(card: CardData): keyof typeof BEHAVIOR_TAGS | null {
  if (card.effectConfig?.isChanceEffect || card.effectConfig?.primary?.isChanceEffect) return 'chance'
  const cat = card.category || card.effectConfig?.category || ''
  if (cat === 'conditional') return 'conditional'
  if (cat === 'streak') return 'streak'
  return null
}

const BehaviorTag: React.FC<{
  label: string
  color: string
  tooltip: string
  fontSize: number
}> = ({ label, color, tooltip, fontSize }) => {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const ref = useRef<HTMLSpanElement>(null)

  const handleEnter = () => {
    if (!ref.current) return
    const rect = ref.current.getBoundingClientRect()
    setPos({ x: rect.left + rect.width / 2, y: rect.top })
    setShow(true)
  }

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{
          fontSize: fontSize - 1, color,
          backgroundColor: `${color}18`,
          padding: '1px 5px',
          borderRadius: '3px',
          border: `1px solid ${color}40`,
          cursor: 'default',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
      {show && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          left: pos.x,
          top: pos.y - 8,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#0f172a',
          border: `1px solid ${color}40`,
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '10px',
          color: '#e2e8f0',
          lineHeight: '1.5',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          zIndex: 10010,
          pointerEvents: 'none',
          fontFamily: 'pressStart',
          maxWidth: '260px',
          textAlign: 'center',
        }}>
          {tooltip}
        </div>,
        document.body
      )}
    </>
  )
}

const EffectNameBadge: React.FC<{
  name: string
  tooltip: string
  color: string
  fontSize: number
}> = ({ name, tooltip, color, fontSize }) => {
  const [show, setShow] = useState(false)
  const [pos, setPos] = useState({ x: 0, y: 0 })
  const ref = useRef<HTMLSpanElement>(null)

  const handleEnter = () => {
    if (!ref.current || !tooltip) return
    const rect = ref.current.getBoundingClientRect()
    setPos({ x: rect.left + rect.width / 2, y: rect.top })
    setShow(true)
  }

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={handleEnter}
        onMouseLeave={() => setShow(false)}
        style={{
          fontSize, fontWeight: '700', color,
          cursor: tooltip ? 'help' : 'default',
        }}
      >
        {name}
      </span>
      {show && tooltip && ReactDOM.createPortal(
        <div style={{
          position: 'fixed',
          left: pos.x,
          top: pos.y - 8,
          transform: 'translate(-50%, -100%)',
          backgroundColor: '#0f172a',
          border: `1px solid ${color}40`,
          borderRadius: '8px',
          padding: '8px 12px',
          fontSize: '10px',
          color: '#e2e8f0',
          lineHeight: '1.5',
          whiteSpace: 'pre-line',
          boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          zIndex: 10010,
          pointerEvents: 'none',
          fontFamily: 'pressStart',
          maxWidth: '280px',
          textAlign: 'center',
        }}>
          {tooltip}
        </div>,
        document.body
      )}
    </>
  )
}

// ─── Edition FX Overlays ──────────────────────────────────────────────────
/// Layered visual effects: Holo=subtle shimmer, Prismatic=iridescent+edge, Diamond=edge+sparkles

const SHIMMER_CONFIGS: Record<string, {
  gradient: string
  duration: string
  opacity: number
}> = {
  // Metallic: a slow, restrained silver light-sweep — a metal glint, no color.
  metallic: {
    gradient: 'linear-gradient(105deg, transparent 42%, rgba(203,213,225,0.035) 47%, rgba(255,255,255,0.08) 50%, rgba(203,213,225,0.035) 53%, transparent 58%)',
    duration: '6.5s',
    opacity: 0.7,
  },
  holographic: {
    gradient: 'linear-gradient(105deg, transparent 35%, rgba(167,139,250,0.06) 45%, rgba(255,255,255,0.08) 50%, rgba(167,139,250,0.06) 55%, transparent 65%)',
    duration: '4.5s',
    opacity: 1,
  },
  prismatic: {
    gradient: 'linear-gradient(105deg, transparent 30%, rgba(244,114,182,0.08) 40%, rgba(167,139,250,0.12) 45%, rgba(96,165,250,0.08) 50%, rgba(52,211,153,0.08) 55%, transparent 65%)',
    duration: '4s',
    opacity: 1,
  },
  diamond: {
    gradient: 'linear-gradient(105deg, transparent 25%, rgba(103,232,249,0.1) 35%, rgba(255,255,255,0.2) 45%, rgba(103,232,249,0.12) 55%, rgba(255,255,255,0.08) 65%, transparent 75%)',
    duration: '3s',
    opacity: 1,
  },
}

const ShimmerOverlay: React.FC<{ edition: string }> = ({ edition }) => {
  const config = SHIMMER_CONFIGS[edition]
  if (!config) return null
  return (
    <div style={{
      position: 'absolute', inset: 0,
      overflow: 'hidden', borderRadius: '11px',
      pointerEvents: 'none', zIndex: 1,
    }}>
      <div style={{
        position: 'absolute',
        top: '-50%', left: '-50%',
        width: '200%', height: '200%',
        background: config.gradient,
        animation: `cardShimmer ${config.duration} ease-in-out infinite`,
        opacity: config.opacity,
      }} />
    </div>
  )
}

const GLOW_CONFIGS: Record<string, {
  color: string
  softColor: string
  duration: string
}> = {
  holographic: {
    color: 'rgba(167,139,250,0.2)',
    softColor: 'rgba(167,139,250,0.08)',
    duration: '4s',
  },
  prismatic: {
    color: 'rgba(244,114,182,0.3)',
    softColor: 'rgba(244,114,182,0.12)',
    duration: '3.5s',
  },
  diamond: {
    color: 'rgba(103,232,249,0.35)',
    softColor: 'rgba(103,232,249,0.15)',
    duration: '2.5s',
  },
}

const SPARKLE_POSITIONS = [
  { top: '12%', left: '18%', delay: '0s', size: 3 },
  { top: '25%', left: '78%', delay: '0.8s', size: 2 },
  { top: '45%', left: '12%', delay: '1.6s', size: 2.5 },
  { top: '60%', left: '85%', delay: '0.4s', size: 3 },
  { top: '75%', left: '35%', delay: '2.0s', size: 2 },
  { top: '88%', left: '65%', delay: '1.2s', size: 2.5 },
  { top: '35%', left: '50%', delay: '2.4s', size: 3 },
  { top: '15%', left: '55%', delay: '1.8s', size: 2 },
]

/**
 * The fantasy-points band at the top of a card back: the season total, and what that
 * came to per game.
 *
 * ⚠️ ONE COMPONENT FOR BOTH BACKS. The vaulted keepsake back and the ordinary back drew
 * this band separately, in two near-identical blocks — which is how one of them would
 * have kept the total alone while the other gained the average.
 *
 * The average is omitted rather than shown as 0.0 when the season recorded no games: a
 * card minted before its player has played is a real state (a rookie print in week 1),
 * and 0.0 there reads as a player who turned up and did nothing.
 */
const FantasyPointsBand: React.FC<{
  stats: NonNullable<CardData['playerStats']>
  fontSize: number
  borderColor: string
  padding: string
  margin: string
  label: string
}> = ({ stats, fontSize, borderColor, padding, margin, label }) => {
  const perGame = stats.fantasyPointsPerGame
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'center',
      gap: perGame != null ? '18px' : 0,
      borderTop: `1px solid ${borderColor}40`,
      borderBottom: `1px solid ${borderColor}40`,
      padding, margin,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize, fontWeight: 700, color: TYPE_COLORS.fp }}>
          {stats.fantasyPoints}
        </div>
        <div style={{ fontSize: fontSize - 5, color: '#94a3b8' }}>{label}</div>
      </div>
      {perGame != null && (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize, fontWeight: 700, color: TYPE_COLORS.fp }}>
            {perGame.toFixed(1)}
          </div>
          <div style={{ fontSize: fontSize - 5, color: '#94a3b8' }}>Per Game</div>
        </div>
      )}
    </div>
  )
}

const SparkleOverlay: React.FC = () => (
  <div style={{
    position: 'absolute', inset: 0,
    pointerEvents: 'none', zIndex: 2,
    borderRadius: '11px', overflow: 'hidden',
  }}>
    {SPARKLE_POSITIONS.map((sp, i) => (
      <div key={i} style={{
        position: 'absolute',
        top: sp.top, left: sp.left,
        width: sp.size, height: sp.size,
        borderRadius: '50%',
        backgroundColor: '#fff',
        boxShadow: '0 0 4px 1px rgba(103,232,249,0.5), 0 0 8px rgba(255,255,255,0.6)',
        animation: `sparkle 2.8s ease-in-out ${sp.delay} infinite`,
        opacity: 0,
      }} />
    ))}
  </div>
)

// Holographic iridescent background overlay — subtle color shift
const HoloBackgroundOverlay: React.FC = () => (
  <div style={{
    position: 'absolute', inset: 0,
    borderRadius: '11px', overflow: 'hidden',
    pointerEvents: 'none', zIndex: 1,
    filter: 'blur(20px)',
  }}>
    <div style={{
      position: 'absolute',
      top: '-25%', left: '-25%',
      width: '150%', height: '150%',
      background: 'conic-gradient(from 0deg, rgba(244,114,182,0.12), rgba(167,139,250,0.25), rgba(96,165,250,0.05), rgba(52,211,153,0.25), rgba(250,204,21,0.12), rgba(244,114,182,0.05))',
      animation: 'holoSpin 10s linear infinite',
      transformOrigin: 'center center',
    }} />
  </div>
)

const HoloEdgeShimmer: React.FC = () => (
  <div style={{
    position: 'absolute', inset: 0,
    borderRadius: '11px', overflow: 'hidden',
    pointerEvents: 'none', zIndex: 2,
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
    WebkitMaskComposite: 'xor',
    padding: '3px',
  }}>
    <div style={{
      position: 'absolute',
      top: '-50%', left: '-50%',
      width: '200%', height: '200%',
      background: 'conic-gradient(from 0deg, transparent 0%, rgba(244,114,182,0.7) 10%, transparent 20%, transparent 50%, rgba(167,139,250,0.7) 60%, transparent 70%)',
      animation: 'holoEdge 4s linear infinite',
      transformOrigin: 'center center',
    }} />
  </div>
)

const DiamondEdgeShimmer: React.FC = () => (
  <div style={{
    position: 'absolute', inset: 0,
    borderRadius: '11px', overflow: 'hidden',
    pointerEvents: 'none', zIndex: 2,
    mask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
    maskComposite: 'exclude',
    WebkitMaskComposite: 'xor',
    padding: '3px',
  }}>
    <div style={{
      position: 'absolute',
      top: '-50%', left: '-50%',
      width: '200%', height: '200%',
      background: 'conic-gradient(from 0deg, transparent 0%, rgba(255,255,255,0.9) 8%, rgba(103,232,249,0.7) 12%, transparent 20%, transparent 45%, rgba(103,232,249,0.8) 53%, rgba(255,255,255,0.9) 58%, transparent 65%)',
      animation: 'holoEdge 3s linear infinite',
      transformOrigin: 'center center',
    }} />
  </div>
)

const TradingCard: React.FC<TradingCardProps> = ({
  card, size = 'md', selected = false, onSelect, onClick, onLevelUp, onTrash, onFindDupes, dupeCount = 0, showSellValue = false, glowColor, staticGlow, noHoverLift, onHoverChange, forceFlipped, apSwapState, gateFP,
}) => {
  const [hovered, setHovered] = useState(false)
  const [flipped, setFlipped] = useState(false)
  // Touch devices have no hover, so hover-gated affordances (select / level-up /
  // trash) must show without it.
  const isMobile = useIsMobile()
  const showActions = hovered || isMobile

  // Sync with external forceFlipped prop (tutorial)
  useEffect(() => {
    if (forceFlipped !== undefined) setFlipped(forceFlipped)
  }, [forceFlipped])
  const rawEdStyle = EDITION_STYLES[card.edition] || EDITION_STYLES.base
  // A synthetic wears its EFFECT's edition, muted — see `syntheticStyle`.
  const edStyle = card.synthetic ? syntheticStyle(rawEdStyle) : rawEdStyle
  const d = SIZES[size]
  const posLabel = POSITION_LABELS[card.position] || '??'
  // Fixed footer height so EVERY card front has identical geometry regardless of how many
  // lines its tagline wraps to — the effect footer and the standard/no-effect placeholder
  // both pin to this. Sized for the worst case (effect-name line + a two-line tagline);
  // the footer vertically CENTERS its content, so a one-line tagline gets balanced padding
  // top and bottom instead of a gap dumped at the card's bottom edge.
  const footerReserve = Math.max(6, d.pad - 5) + Math.max(4, d.pad - 7) + Math.round((d.font + 1) * 1.35) + 2 + Math.round((d.font - 3) * 1.3) * 2
  const effectDisplayName = card.displayName || card.effectConfig?.displayName || ''
  const effectTagline = card.tagline || card.effectConfig?.tagline || ''
  const effectTooltip = card.tooltip || card.effectConfig?.tooltip || ''
  const effectDetail = card.detail || card.effectConfig?.detail || ''
  const gateText = card.gateText || card.effectConfig?.gateText || ''
  // FP power-bar gate: {threshold, inverse} frozen into the effect config at mint.
  // `gateOff` is true only in a live scoring context (gateFP known) where the
  // depicted player hasn't met the bar this week — the effect scores nothing.
  const gate: { threshold?: number; inverse?: boolean } | undefined = card.effectConfig?.gate
  const gateThreshold = gate?.threshold ?? 0
  const gateOff = gateThreshold > 0 && gateFP != null &&
    (gate?.inverse ? gateFP >= gateThreshold : gateFP < gateThreshold)
  const category = card.category || card.effectConfig?.category || ''
  const categoryColor = CATEGORY_COLORS[category] || '#94a3b8'
  const outputType = card.outputType || card.effectConfig?.outputType || ''
  const outputTypeColor = TYPE_COLORS[outputType] || categoryColor
  const behaviorKey = getBehaviorTag(card)
  const behaviorTag = behaviorKey ? BEHAVIOR_TAGS[behaviorKey] : null

  const stars = card.ratingStars || calcStars(card.playerRating)
  const tierColor = getTierColor(card.playerRating)

  // Vaulted cards drop their effect and become keepsake player cards: no effect
  // text, no behavior tags, no upgrade-tier chrome — just the player + stats.
  const isVaulted = !!card.vaulted

  // Glitched cards take the AWAKENED treatment's shape (a glow that breathes in
  // intensity and never moves) rather than the glitch-feed animations, which sway and
  // strobe — fine for a line passing through a feed, unpleasant on a card someone is
  // reading for minutes. See the note in index.css.
  const glitchClass = card.glitched
    ? ['glitched-card',
       card.glitchAwakened ? 'glitched-card-awakened' : '',
       card.glitchSurged ? 'glitched-card-surged' : ''].filter(Boolean).join(' ')
    : ''

  // Upgrade tier: hexagon badge shown for tier 2+ (un-upgraded base cards stay
  // clean), full gold ring added at the max tier (IV) to flag a fully-upgraded card.
  const cardTier = card.tier || 1
  const showTierBadge = cardTier >= 2 && !isVaulted
  const isMaxTier = cardTier >= 4
  const tb = TIER_BADGE_DIMS[size]
  // Gold ring (box-shadow, sits just outside the edition border) for max tier.
  const tier4Ring = (isMaxTier && !isVaulted)
    ? '0 0 0 2px #fbbf24, 0 0 16px rgba(251,191,36,0.55), '
    : ''

  const edition = card.edition
  // ⚠️ NO EDITION GLOW ON A SYNTHETIC, for the same reason as the foil below: `GLOW_CONFIGS`
  // is keyed by edition, and a synthetic is minted AT its effect's edition — so a synthetic
  // diamond kept the diamond halo and read as a real pull from across the collection.
  const glowConfig = card.synthetic ? undefined : GLOW_CONFIGS[edition]
  const hasGlow = !!glowConfig

  // Depth: a top light-sheen over the edition gradient + inset highlight/vignette
  // so the card reads as a physical print rather than a flat panel.
  const depthInset = 'inset 0 1px 0 rgba(255,255,255,0.13), inset 0 -34px 54px -22px rgba(0,0,0,0.55), '

  const containerStyle: React.CSSProperties = {
    width: d.width,
    height: d.height,
    borderRadius: '12px',
    border: `2px solid ${selected ? '#3b82f6' : edStyle.borderColor}`,
    // ⚠️ The schematic grid layers OVER the ground for a synthetic, and the usual
    // top-light sheen is dropped — a blueprint is lit flat, and the sheen is the gloss a
    // real pull has.
    background: card.synthetic
      ? `${syntheticGrid(rawEdStyle.borderColor)}, ${edStyle.bgGradient}`
      : `radial-gradient(120% 70% at 50% -10%, rgba(255,255,255,0.10), transparent 60%), ${edStyle.bgGradient}`,
    fontFamily: 'pressStart',
    cursor: 'pointer',
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    transition: 'transform 0.15s, box-shadow 0.25s',
    transform: !noHoverLift && hovered ? 'translateY(-4px)' : 'none',

    boxShadow: depthInset + tier4Ring + (selected
      ? '0 0 0 2px #3b82f6, 0 4px 20px rgba(59,130,246,0.3)'
      : hasGlow && hovered
        ? `0 0 12px ${glowConfig.color}, 0 0 28px ${glowConfig.softColor}, 0 4px 20px ${glowConfig.color}`
        : hasGlow
          ? `0 0 6px ${glowConfig.softColor}, 0 2px 8px rgba(0,0,0,0.3)`
          : edStyle.glowColor && hovered
            ? `0 4px 20px ${edStyle.glowColor}`
            : '0 2px 8px rgba(0,0,0,0.3)'),
    opacity: (card.isActive || card.vaulted) ? 1 : 0.7,
    flexShrink: 0,
  }

  // When an onClick prop is provided (e.g. pack-selection flow), reserve
  // the rightmost 25% of the card as a flip-only zone so the user can still
  // inspect the back without losing access to whatever the parent wired up.
  // No onClick prop ⇒ legacy behavior: click anywhere flips.
  const handleCardClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onClick) {
      setFlipped(f => !f)
      return
    }
    const rect = e.currentTarget.getBoundingClientRect()
    const relX = (e.clientX - rect.left) / rect.width
    if (relX >= 0.75) {
      setFlipped(f => !f)
    } else {
      onClick()
    }
  }

  const gm = GLITCH_MARK_DIMS[size]

  return (
    <div
      className={glitchClass || undefined}
      style={containerStyle}
      onClick={handleCardClick}
      onMouseEnter={() => { setHovered(true); onHoverChange?.(true) }}
      onMouseLeave={() => { setHovered(false); onHoverChange?.(false) }}
    >
      {/* Glitch marker — the at-a-glance signal that this card caught something. */}
      {card.glitched && (
        <GlitchMark
          size={gm.size}
          top={gm.top}
          right={gm.right}
          awakened={card.glitchAwakened}
          title={card.glitchedSeason
            ? `Glitched in the Season ${card.glitchedSeason} Criticality (week ${card.glitchedWeek})`
            : 'Glitched during a Criticality'}
        />
      )}

      {/* Flip-zone affordance — only when an external onClick is wired up.
          Subtle vertical strip on the right edge with a flip glyph so users
          discover the zone without ugly visual noise. */}
      {onClick && (
        <div style={{
          position: 'absolute',
          top: 0, right: 0, bottom: 0,
          width: '25%',
          pointerEvents: 'none',
          zIndex: 4,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-end',
          padding: '6px',
          opacity: hovered ? 0.8 : 0.4,
          transition: 'opacity 0.15s',
        }}>
          <div style={{
            fontSize: '11px',
            color: '#94a3b8',
            background: 'rgba(15,23,42,0.7)',
            border: '1px solid rgba(148,163,184,0.3)',
            borderRadius: '4px',
            padding: '2px 5px',
            lineHeight: 1,
          }}>
            ↻
          </div>
        </div>
      )}

      {/* Edition FX overlays */}
      {/* ⚠️ NONE OF THESE ON A SYNTHETIC. They key off `edition`, and a synthetic is minted
          AT its effect's edition — so a synthetic diamond kept the diamond sparkle and the
          edge shimmer on top of the blueprint, which is the exact identity the ruling
          removes. The foil is the loudest thing a real pull has; a built card does not get
          to wear it. */}
      {!card.synthetic && edition === 'metallic' && <ShimmerOverlay edition={edition} />}
      {!card.synthetic && edition === 'holographic' && <ShimmerOverlay edition={edition} />}
      {!card.synthetic && edition === 'prismatic' && <><HoloBackgroundOverlay /><HoloEdgeShimmer /></>}
      {!card.synthetic && edition === 'diamond' && <><DiamondEdgeShimmer /><SparkleOverlay /></>}

      {/* Upgrade-tier hexagon badge (tier 2+), pinned under the header divider.
          Brighter + glowing at max tier, which also gets a full gold ring
          (see tier4Ring on the container). */}
      {showTierBadge && (
        <div style={{
          position: 'absolute', top: tb.top, left: tb.left, zIndex: 5,
          width: tb.w, height: tb.h,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: tb.font, fontWeight: 800, fontFamily: 'pressStart',
          color: '#1a1206', letterSpacing: '0.5px',
          clipPath: HEX_CLIP, WebkitClipPath: HEX_CLIP,
          background: isMaxTier
            ? 'linear-gradient(135deg, #fde68a 0%, #f59e0b 60%, #d97706 100%)'
            : 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)',
          filter: isMaxTier
            ? 'drop-shadow(0 0 4px rgba(251,191,36,0.85))'
            : 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
          pointerEvents: 'none',
        }}>
          {TIER_ROMAN[cardTier] || cardTier}
        </div>
      )}

      {/* Header: edition + season + rookie */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: `${d.pad - 2}px ${d.pad}px`,
        borderBottom: `1px solid ${edStyle.borderColor}40`,
        position: 'relative', zIndex: 3,
      }}>
        {/* ⚠️ THE TOP-LEFT SAYS SYNTHETIC, NOT THE EDITION (owner). The card still WEARS
            its effect's edition everywhere else — the muted palette, the power scale, the
            gate — but the label is the one place a built card has to announce itself,
            because that is the corner a collector reads first. The edition is still in the
            tooltip.
            ⚠️ The tooltip builds from `rawEdStyle`, NOT `edStyle` — `syntheticStyle`
            already appends " SNTH" to the label, so the styled one reads
            "SYNTHETIC DIAMOND SNTH". */}
        <EditionBadge
          label={edStyle.label}
          rarity={edStyle.rarity}
          color={edStyle.labelColor}
          fontSize={d.font - 2}
          tooltipOverride={card.synthetic
            ? `SYNTHETIC ${rawEdStyle.label.toUpperCase()}`
            : undefined}
        />
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          {parseClassifications(card.classification, card.isRookie, card.synthetic).map(key => {
            const cfg = CLASSIFICATION_CONFIG[key]
            if (!cfg) return null
            return (
              <ClassificationBadge
                key={key}
                abbr={cfg.abbr}
                color={cfg.color}
                bgColor={cfg.bgColor}
                borderColor={cfg.borderColor}
                tooltip={cfg.tooltip}
                fontSize={d.font - 3}
                swapState={key === 'all_pro' ? apSwapState : undefined}
              />
            )
          })}
          <span style={{
            fontSize: d.font - 3, fontWeight: 700, color: '#cbd5e1',
            backgroundColor: 'rgba(148,163,184,0.18)',
            border: '1px solid rgba(148,163,184,0.35)',
            borderRadius: '3px', padding: '1px 4px', lineHeight: 1.2,
          }}>S{card.seasonCreated}</span>
        </div>
      </div>

      {/* ── Card front ── */}
      {!flipped && (
        <>
          <div style={{
            flex: '0 1 auto', display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center',
            padding: `${d.pad}px`,
            textAlign: 'center',
            gap: '4px',
            position: 'relative', zIndex: 3,
            minHeight: 0, overflow: 'hidden',
          }}>
            {/* Team avatar — center medallion (replaces player initials) */}
            <div style={{
              width: d.avatar, height: d.avatar,
              flexShrink: 0,
              borderRadius: '50%',
              boxShadow: glowColor
                ? `0 0 8px ${hexToRgba(glowColor, 0.6)}, 0 0 18px ${hexToRgba(glowColor, 0.3)}`
                : 'none',
              animation: glowColor && !staticGlow ? 'matchGlowPulse 2.5s ease-in-out infinite' : 'none',
              ['--glow-color' as any]: glowColor ? hexToRgba(glowColor, 0.6) : undefined,
              ['--glow-color-soft' as any]: glowColor ? hexToRgba(glowColor, 0.3) : undefined,
              ['--glow-color-bright' as any]: glowColor ? hexToRgba(glowColor, 0.9) : undefined,
              marginBottom: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}>
              {card.teamId ? (
                <img
                  src={`/avatars/${card.teamId}.png`}
                  alt=""
                  crossOrigin="anonymous"
                  style={{ width: '90%', height: '90%', objectFit: 'contain' }}
                />
              ) : null}
            </div>
          </div>

          {/* Flexible spacer — splits the leftover vertical space so the identity group
              floats in the middle instead of packing under the logo. */}
          <div style={{ flex: 1, minHeight: 0 }} />

          {/* Position — colored tag centered directly above the name */}
          <div style={{
            display: 'flex', justifyContent: 'center',
            padding: `0 ${d.pad}px ${Math.max(2, d.pad - 6)}px`,
            flexShrink: 0, position: 'relative', zIndex: 3,
          }}>
            {(() => {
              const posColor = POSITION_COLORS[card.position] || '#94a3b8'
              return (
                <span style={{
                  fontSize: d.font, fontWeight: 800, color: '#f8fafc',
                  letterSpacing: '0.02em',
                  background: `linear-gradient(135deg, ${hexToRgba(posColor, 0.9)}, ${hexToRgba(posColor, 0.6)})`,
                  border: `1px solid ${hexToRgba(posColor, 0.9)}`,
                  borderRadius: '5px', padding: '1px 10px',
                  boxShadow: `0 0 8px ${hexToRgba(posColor, 0.3)}`,
                  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                }}>
                  {posLabel}
                </span>
              )
            })()}
          </div>

          {/* Nameplate band — the player's name as a bold, edition-tinted banner.
              Fixed minHeight (sized for the largest font) so a shrunk long-name
              font doesn't collapse the band; name is vertically centered. */}
          <div style={{
            padding: `${d.pad - 3}px ${d.pad}px`,
            minHeight: Math.round(d.nameFont * 1.25) + 2 * (d.pad - 3),
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(90deg, ${edStyle.borderColor}30, rgba(5,8,14,0.5))`,
            borderTop: `1px solid ${edStyle.borderColor}55`,
            textAlign: 'center', position: 'relative', zIndex: 3, flexShrink: 0,
            boxSizing: 'border-box',
          }}>
            <div style={{
              fontSize: card.playerName.length > 18 ? d.nameFont - 4
                : card.playerName.length > 14 ? d.nameFont - 2
                : d.nameFont,
              fontWeight: '800', color: '#fff',
              lineHeight: 1.05, maxWidth: '100%', wordBreak: 'break-word',
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            }}>
              {card.playerName}
            </div>
          </div>

          {/* Quality stars — centered on their own row (position lives in the header). */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '2px',
            padding: `2px ${d.pad}px`,
            flexShrink: 0, position: 'relative', zIndex: 3,
          }}>
            {Array.from({ length: 5 }, (_, i) => (
              <span key={i} style={{
                fontSize: d.starSize, lineHeight: 1,
                color: i < stars ? tierColor : '#4d5a6e',
              }}>
                ★
              </span>
            ))}
          </div>

          {/* Flexible spacer — pushes the fixed footer to the bottom, balancing the
              space above the identity group. */}
          <div style={{ flex: 1, minHeight: 0 }} />

          {/* Effect footer — hidden on vaulted (effect gone) and on the sub-base
              "base" (no-effect) print, which just fields the player for their FP. */}
          {!isVaulted && edition !== 'base' && (
          <div style={{
            padding: `${Math.max(6, d.pad - 5)}px ${d.pad + 18}px ${Math.max(4, d.pad - 7)}px`,
            height: footerReserve, boxSizing: 'border-box', overflow: 'hidden',
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
            borderTop: `1px solid ${edStyle.borderColor}40`,
            textAlign: 'center',
            position: 'relative', zIndex: 3,
            flexShrink: 0,
          }}>
            {/* Effect name + tagline dim when the power bar isn't met this week
                (the effect scores nothing); the bar itself stays lit as the "why". */}
            <div style={{ opacity: gateOff ? 0.4 : 1 }}>
              {effectDisplayName && (
                <div style={{ marginBottom: '2px' }}>
                  <EffectNameBadge
                    name={effectDisplayName}
                    tooltip={behaviorTag ? `${effectTooltip}\n\n${behaviorTag.tooltip}` : effectTooltip}
                    color={outputTypeColor}
                    fontSize={d.font + 1}
                  />
                </div>
              )}
              {effectTagline && (
                <div style={{
                  fontSize: d.font - 3, color: edStyle.labelColor,
                  lineHeight: 1.3,
                  // Natural height, clamped to two lines. The footer's fixed height +
                  // vertical centering keeps every card's geometry identical, so this
                  // no longer needs a rigid two-line box.
                  overflow: 'hidden',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {colorizeEffectText(effectTagline, edStyle.labelColor)}
                </div>
              )}
            </div>
            {/* The FP power bar renders BELOW the card body (not here) so it can't
                compress the footer. tierNote intentionally not shown on the front. */}
          </div>
          )}

          {/* Standard (no-effect) print: reserve the footer's space so the nameplate
              lands at the same height as effect cards. minHeight ≈ a typical
              two-line effect footer; left intentionally empty (no "No Effect" label). */}
          {!isVaulted && edition === 'base' && (
          <div style={{
            padding: `${d.pad - 2}px ${d.pad + 18}px`,
            minHeight: footerReserve,
            boxSizing: 'border-box',
            borderTop: `1px solid ${edStyle.borderColor}40`,
            position: 'relative', zIndex: 3, flexShrink: 0,
          }} />
          )}

          {/* Sell value / expired / equipped badges */}
          {(showSellValue || (!card.isActive && !card.vaulted) || card.isEquipped) && (
            <div style={{
              position: 'absolute', bottom: d.pad - 2, right: d.pad,
              display: 'flex', gap: '4px', alignItems: 'center',
            }}>
              {card.isEquipped && (
                <span style={{
                  fontSize: d.font - 4, color: '#60a5fa',
                  backgroundColor: 'rgba(96,165,250,0.15)', padding: '1px 4px',
                  borderRadius: '3px', border: '1px solid rgba(96,165,250,0.3)',
                }}>
                  Equipped
                </span>
              )}
              {!card.isActive && !card.vaulted && (
                <span style={{
                  fontSize: d.font - 4, color: '#ef4444',
                  backgroundColor: 'rgba(239,68,68,0.15)', padding: '1px 4px',
                  borderRadius: '3px', border: '1px solid rgba(239,68,68,0.3)',
                }}>
                  Expired
                </span>
              )}
              {showSellValue && !card.isEquipped && !card.vaulted && (
                <span style={{ fontSize: d.font - 3, color: '#eab308', fontWeight: '600' }}>
                  {card.sellValue}
                </span>
              )}
            </div>
          )}

          {/* Select overlay for collection selling — visible on hover or when selected */}
          {onSelect && !card.isEquipped && !card.vaulted && (showActions || selected) && (
            <button
              onClick={(e) => { e.stopPropagation(); onSelect(e) }}
              style={{
                position: 'absolute',
                top: d.pad - 2,
                right: d.pad - 2,
                zIndex: 4,
                width: '20px',
                height: '20px',
                borderRadius: '4px',
                border: `2px solid ${selected ? '#3b82f6' : '#475569'}`,
                backgroundColor: selected ? '#3b82f6' : 'rgba(15,23,42,0.7)',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 0,
                transition: 'all 0.15s',
              }}
            >
              {selected && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </button>
          )}

          {/* Bottom-left hover actions (collection): the duplicates finder stacked above
              Level Up. Stacked rather than side by side because the sell value owns the
              bottom-right, and on a small card the three do not fit on one line. */}
          {!card.vaulted && showActions && (onLevelUp || (onFindDupes && dupeCount > 0)) && (
          <div style={{
            position: 'absolute', bottom: d.pad - 2, left: d.pad - 2, zIndex: 4,
            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px',
          }}>
          {onFindDupes && dupeCount > 0 && (
            <HoverTooltip text={`Show your ${dupeCount} other ${card.displayName || card.effectConfig?.displayName || ''} card${dupeCount === 1 ? '' : 's'}`}>
              <button
                onClick={(e) => { e.stopPropagation(); onFindDupes() }}
                aria-label={`Show ${dupeCount} duplicate${dupeCount === 1 ? '' : 's'}`}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '3px 7px',
                  borderRadius: '5px',
                  border: '1px solid rgba(94,234,212,0.55)',
                  background: 'rgba(15,23,42,0.88)',
                  color: '#5eead4',
                  fontSize: d.font - 3, fontWeight: 800,
                  fontFamily: 'pressStart', cursor: 'pointer',
                }}
              >
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                  <rect x="1" y="3" width="6.5" height="8" rx="1" stroke="#5eead4" strokeWidth="1.4"/>
                  <path d="M4 1.2h5.6a1 1 0 011 1v7" stroke="#5eead4" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
                &times;{dupeCount}
              </button>
            </HoverTooltip>
          )}
          {/* Level-Up affordance (collection) — gold pill */}
          {onLevelUp && (
            <button
              onClick={(e) => { e.stopPropagation(); onLevelUp() }}
              aria-label="Level Up"
              style={{
                display: 'flex', alignItems: 'center', gap: '3px',
                padding: '3px 7px',
                borderRadius: '5px',
                border: '1px solid rgba(251,191,36,0.6)',
                background: 'linear-gradient(135deg, rgba(251,191,36,0.9), rgba(217,119,6,0.9))',
                color: '#1a1206',
                fontSize: d.font - 3, fontWeight: 800,
                fontFamily: 'pressStart', cursor: 'pointer',
                boxShadow: '0 0 8px rgba(251,191,36,0.4)',
              }}
            >
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path d="M6 2v8M6 2L3 5M6 2l3 3" stroke="#1a1206" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Level Up
            </button>
          )}
          </div>
          )}

          {/* Trash affordance (vault) — red icon button, bottom-right on hover */}
          {onTrash && showActions && (
            <button
              onClick={(e) => { e.stopPropagation(); onTrash() }}
              aria-label="Remove from vault (permanent)"
              style={{
                position: 'absolute',
                bottom: d.pad - 2,
                right: d.pad - 2,
                zIndex: 4,
                width: '24px', height: '24px', borderRadius: '5px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                border: '1px solid rgba(239,68,68,0.55)',
                background: 'rgba(15,23,42,0.85)',
                color: '#ef4444', cursor: 'pointer',
              }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M4 7h16M9 7V5a1 1 0 011-1h4a1 1 0 011 1v2M6 7l1 13a1 1 0 001 1h8a1 1 0 001-1l1-13" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </>
      )}

      {/* ── Card back: player stats (vaulted keepsake) ── */}
      {flipped && isVaulted && (
        <div style={{
          flex: 1, display: 'flex', flexDirection: 'column',
          padding: `${d.pad}px`, gap: '6px',
          overflowY: 'auto', position: 'relative', zIndex: 3,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: d.font, fontWeight: 700, color: '#e2e8f0', lineHeight: 1.3 }}>
              {card.playerName}
            </div>
            <div style={{ fontSize: d.font - 3, color: edStyle.labelColor, marginTop: '2px' }}>
              {posLabel}
              {card.playerStats?.teamName && (
                <>
                  {', '}
                  <span style={{ color: card.playerStats.teamColor || edStyle.labelColor }}>
                    {card.playerStats.teamName}
                  </span>
                </>
              )}
            </div>
            {/* Player star rating */}
            <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', marginTop: '4px' }}>
              {Array.from({ length: stars }, (_, i) => (
                <span key={i} style={{ fontSize: d.starSize - 8, color: tierColor }}>★</span>
              ))}
            </div>
            <div style={{
              fontSize: d.font - 3, fontWeight: 700, color: '#94a3b8',
              textTransform: 'uppercase', letterSpacing: '0.02em', marginTop: '4px',
            }}>
              Season {card.playerStats?.season ?? card.seasonCreated} Stats
            </div>
          </div>

          {card.playerStats ? (
            <>
              {/* Season fantasy points, total and per game */}
              <FantasyPointsBand
                stats={card.playerStats}
                fontSize={d.font + 1}
                borderColor={edStyle.borderColor}
                padding="6px 0"
                margin="2px 0"
                label="Season FP"
              />

              {/* Stat lines */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px' }}>
                {card.playerStats.lines.map((ln, i) => (
                  <div key={i} style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                    fontSize: d.font - 1,
                  }}>
                    <span style={{ color: '#94a3b8' }}>{ln.label}</span>
                    <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{ln.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={{
              flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: d.font - 1, color: '#64748b', textAlign: 'center', lineHeight: 1.6,
            }}>
              No stats recorded for Season {card.seasonCreated}
            </div>
          )}
        </div>
      )}

      {/* ── Card back (effect details) ── */}
      {flipped && !isVaulted && (
        <div
          style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            padding: `${d.pad}px`,
            gap: '8px',
            overflowY: 'auto',
            position: 'relative', zIndex: 3,
          }}
        >
          {/* Header — effect name */}
          {effectDisplayName && (
            <div style={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' }}>
              <EffectNameBadge
                name={effectDisplayName}
                tooltip={effectTooltip}
                color={outputTypeColor}
                fontSize={d.font - 1}
              />
              {behaviorTag && (
                <BehaviorTag
                  label={behaviorTag.label}
                  color={behaviorTag.color}
                  tooltip={behaviorTag.tooltip}
                  fontSize={d.font - 2}
                />
              )}
            </div>
          )}

          {/* Detail */}
          {effectDetail && (
            <div style={{
              fontSize: d.font - 1, color: '#cbd5e1',
              lineHeight: 1.6, textAlign: 'center',
            }}>
              {colorizeEffectText(effectDetail, '#cbd5e1')}
            </div>
          )}

          {/* FP power-bar requirement — its own line, tied to the green power bar */}
          {gateText && (
            <div style={{
              fontSize: d.font - 2, color: '#7fd8a0', fontStyle: 'italic',
              lineHeight: 1.4, textAlign: 'center', marginTop: '2px',
            }}>
              {gateText}
            </div>
          )}

          {/* Upgrade-tier line (own distinct line) */}
          {card.tierNote && (
            <div style={{
              fontSize: d.font - 1, fontWeight: 700, color: '#fbbf24',
              textAlign: 'center', lineHeight: 1.3,
            }}>
              {card.tierNote}
            </div>
          )}

          {/* Season stats — the player's production for this card's season, same
              data the vaulted back shows (fills the space Roster Match vacated). */}
          <div style={{ marginTop: 'auto', paddingTop: '6px' }}>
            <div style={{
              fontSize: d.font - 4, fontWeight: 700, color: '#94a3b8',
              textTransform: 'uppercase', letterSpacing: '0.02em',
              textAlign: 'center', marginBottom: '5px',
            }}>
              Season {card.playerStats?.season ?? card.seasonCreated} Stats
            </div>
            {card.playerStats ? (
              <>
                <FantasyPointsBand
                  stats={card.playerStats}
                  fontSize={d.font}
                  borderColor={edStyle.borderColor}
                  padding="5px 0"
                  margin="0 0 5px"
                  label="Fantasy Points"
                />
                <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                  {card.playerStats.lines.map((ln, i) => (
                    <div key={i} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                      fontSize: d.font - 2,
                    }}>
                      <span style={{ color: '#94a3b8' }}>{ln.label}</span>
                      <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{ln.value}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div style={{
                fontSize: d.font - 2, color: '#64748b', textAlign: 'center',
                lineHeight: 1.5, padding: '4px 0',
              }}>
                No stats recorded yet
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  )
}

export default TradingCard
